import api from "./axios";

export interface Exam {
    id: string;
    class_subject_id: string;
    teacher_id: string;
    title: string;
    description?: string;
    instructions?: string;
    duration_minutes: number;
    max_attempts: number;
    start_time: string;
    end_time: string;
    weight: number;
    max_score: number;
    randomize_questions: boolean;
    randomize_options: boolean;
    show_results_immediately: boolean;
    show_correct_answers: boolean;
    max_violations: number;
    is_published: boolean;
    passing_score?: number;
    enforce_passing: boolean;
    status: "draft" | "scheduled" | "ongoing" | "closed" | "archived";
    created_at: string;
    updated_at: string;
    questions?: {
        id: string;
        question_bank_id: string;
        order: number;
        points: number;
        question_bank: {
            id: string;
            question: string;
            type: string;
            type_display?: string;
            difficulty: string;
            difficulty_display?: string;
            points: number;
            tags?: string[];
            explanation?: string;
            is_active: boolean;
            subject?: {
                id: string;
                name: string;
                code: string;
            };
            teacher?: {
                id: string;
                nip: string;
                user: {
                    id: string;
                    name: string;
                    email: string;
                };
            };
        };
    }[];

    // Relationships
    class_subject?: {
        id: string;
        subject: {
            id: string;
            name: string;
            code: string;
        };
        class: {
            id: string;
            name: string;
            grade_level: string;
        };
    };
    teacher?: {
        id: string;
        teacher_id: string;
        nip: string;
        user: {
            id: string;
            name: string;
            email: string;
        };
    };

    // Computed attributes
    duration_in_hours?: number;
    total_points?: number;
    question_count?: number;
    average_score?: number;
    highest_score?: number;
    lowest_score?: number;

    // Status helpers
    is_draft?: boolean;
    is_scheduled?: boolean;
    is_ongoing?: boolean;
    is_closed?: boolean;
    is_archived?: boolean;
    is_active?: boolean;
    has_started?: boolean;
    has_ended?: boolean;
    is_time_scheduled?: boolean;

    // Attempt-based locking fields
    total_attempt_count?: number;
    has_any_attempts?: boolean;
    questions_locked?: boolean;
}

export interface CreateExamData {
    title: string;
    description?: string;
    instructions?: string;
    class_subject_id: string;
    teacher_id: string;
    duration_minutes: number;
    max_attempts: number;
    start_time: string;
    end_time: string;
    weight: number;
    max_score: number;
    randomize_questions?: boolean;
    randomize_options?: boolean;
    show_results_immediately?: boolean;
    show_correct_answers?: boolean;
    max_violations: number;
    is_published?: boolean;
    passing_score?: number | null;
    enforce_passing?: boolean;
    status?: "draft" | "scheduled" | "archived";
    questions?: {
        question_bank_id: string;
        points: number;
    }[];
}

// For teachers creating exams - only questions and class_subject_id are editable
export interface TeacherExamData {
    class_subject_id: string;
    status?: "draft" | "scheduled";
    is_published?: boolean;
    questions: {
        question_bank_id: string;
        points: number;
    }[];
}

export interface UpdateExamData {
    title?: string;
    description?: string;
    instructions?: string;
    class_subject_id?: string;
    teacher_id?: string;
    duration_minutes?: number;
    max_attempts?: number;
    start_time?: string;
    end_time?: string;
    weight?: number;
    max_score?: number;
    randomize_questions?: boolean;
    randomize_options?: boolean;
    show_results_immediately?: boolean;
    show_correct_answers?: boolean;
    max_violations?: number;
    is_published?: boolean;
    passing_score?: number | null;
    enforce_passing?: boolean;
}

export interface ExamFilters {
    search?: string;
    class_id?: string;
    subject_id?: string;
    status?: "draft" | "scheduled" | "ongoing" | "closed" | "archived";
    teacher_id?: string;
    page?: number;
    per_page?: number;
}

export const examApi = {
    async getAll(filters: ExamFilters = {}): Promise<{
        success: boolean;
        message: string;
        data: { items: Exam[]; meta: any };
    }> {
        const response = await api.get("/v1/exams", { params: filters });
        return response.data;
    },

    async getById(id: string): Promise<Exam> {
        const response = await api.get(`/v1/exams/${id}`);
        return response.data.data;
    },

    async create(data: CreateExamData | TeacherExamData): Promise<Exam> {
        const response = await api.post("/v1/exams", data);
        return response.data.data;
    },

    async update(id: string, data: UpdateExamData): Promise<Exam> {
        const response = await api.put(`/v1/exams/${id}`, data);
        return response.data.data;
    },

    async delete(id: string): Promise<void> {
        await api.delete(`/v1/exams/${id}`);
    },

    async publish(id: string): Promise<Exam> {
        const response = await api.post(`/v1/exams/${id}/publish`);
        return response.data.data;
    },

    async getStatistics(id: string): Promise<any> {
        const response = await api.get(`/v1/exams/${id}/statistics`);
        return response.data.data;
    },

    async updateStatuses(): Promise<{ updated_count: number }> {
        const response = await api.post("/v1/exams/update-statuses");
        return response.data.data;
    },

    async getActive(): Promise<Exam[]> {
        const response = await api.get("/v1/exams/active");
        return response.data.data?.items || [];
    },

    async getScheduled(): Promise<Exam[]> {
        const response = await api.get("/v1/exams/scheduled");
        return response.data.data?.items || [];
    },

    async getEnded(): Promise<Exam[]> {
        const response = await api.get("/v1/exams/ended");
        return response.data.data?.items || [];
    },

    async getStudentExams(): Promise<Exam[]> {
        const response = await api.get("/v1/student-exams");
        return response.data.data || [];
    },

    async updateStatus(id: string, status: string): Promise<Exam> {
        const response = await api.patch(`/v1/exams/${id}/status`, { status });
        return response.data.data;
    },
};

export default examApi;
